import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from '@/components/ui/dialog';
import { Search, Lock, AlertTriangle, RefreshCw, ScanLine } from 'lucide-react';
import { api, PlanUsage, ScanMode } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTriggerAnalysis } from '@/hooks/useTriggerAnalysis';
import { ScanModeChoice } from '@/components/ScanModeChoice';

interface Repository {
  id: string;
  name: string;
  platform_type: string;
  full_name: string;
  is_private: boolean;
  analysis_enabled: boolean;
}

interface ScanRepositoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScanComplete?: () => void;
}

const PLATFORM_BADGE: Record<string, { bg: string; color: string }> = {
  github:      { bg: 'rgba(255,255,255,.06)', color: '#8b949e' },
  gitlab:      { bg: 'rgba(252,109,38,.12)',  color: '#FC6D26' },
  azure_devops:{ bg: 'rgba(0,120,215,.12)',   color: '#0078D7' },
};

const ScanRepositoryModal: React.FC<ScanRepositoryModalProps> = ({ open, onOpenChange, onScanComplete }) => {
  const { triggerAnalysis } = useTriggerAnalysis();
  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [selectedRepoIds, setSelectedRepoIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [billingStatus, setBillingStatus] = useState<PlanUsage | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanMode, setScanMode] = useState<ScanMode>('incremental');
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      fetchRepositories();
      setSelectedRepoIds(new Set());
      setSearchQuery('');
      setScanMode('incremental');
      api.getBillingStatus().then(setBillingStatus).catch(console.error);
    }
  }, [open]);

  const fetchRepositories = async () => {
    setLoadingRepos(true);
    try {
      const response = await api.get<{ repositories: Repository[] }>('/api/repositories?page=1&page_size=100');
      setRepositories(response.data.repositories || []);
    } catch {
      toast.error('Failed to load repositories');
    } finally {
      setLoadingRepos(false);
    }
  };

  const handleToggleRepository = (repoId: string) => {
    const next = new Set(selectedRepoIds);
    if (next.has(repoId)) next.delete(repoId);
    else next.add(repoId);
    setSelectedRepoIds(next);
  };

  const filteredRepositories = repositories.filter(repo =>
    repo.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    repo.full_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeRepositories = filteredRepositories.filter(r => r.analysis_enabled);
  const inactiveRepositories = filteredRepositories.filter(r => !r.analysis_enabled);

  const allActiveSelected = activeRepositories.length > 0 && activeRepositories.every(r => selectedRepoIds.has(r.id));

  const handleToggleAll = () => {
    const next = new Set(selectedRepoIds);
    if (allActiveSelected) {
      activeRepositories.forEach(r => next.delete(r.id));
    } else {
      activeRepositories.forEach(r => next.add(r.id));
    }
    setSelectedRepoIds(next);
  };

  const handleScan = async () => {
    if (selectedRepoIds.size === 0) return;
    setScanning(true);

    let successCount = 0;
    let failCount = 0;

    await Promise.all(
      Array.from(selectedRepoIds).map(async (repoId) => {
        const repo = repositories.find(r => r.id === repoId);
        try {
          await triggerAnalysis(repoId, repo?.name, onScanComplete, scanMode);
          successCount++;
        } catch {
          failCount++;
        }
      })
    );

    setScanning(false);
    onOpenChange(false);
    if (onScanComplete) onScanComplete();

    if (failCount > 0) {
      toast.error(`Started ${successCount} scans, failed ${failCount}.`);
    } else {
      toast.success(`Successfully triggered analysis for ${successCount} repositories.`);
    }
  };

  const usagePct = billingStatus && billingStatus.max_repos !== -1
    ? Math.round((billingStatus.active_repos / billingStatus.max_repos) * 100)
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col gap-0 p-0 sm:max-w-[520px] max-h-[88vh] !bg-[#111115] rounded-[12px] border border-[rgba(255,255,255,.08)] shadow-[0_24px_64px_rgba(0,0,0,.7)] [&>button]:hidden">

        {/* ── Header ── */}
        <div className="flex items-center justify-between border-b border-[rgba(255,255,255,.08)] px-5 py-[14px]">
          <div className="flex items-center gap-[9px]">
            <div className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[6px] border border-[rgba(255,255,255,.08)] bg-[#17171c]">
              <ScanLine className="h-[14px] w-[14px] text-[#b5e84a]" />
            </div>
            <div>
              <div className="text-[14px] font-semibold leading-none">New Scan</div>
              <div className="mt-[3px] font-mono text-[10px] text-muted-foreground">
                Select repositories to analyse for technical debt
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="grid h-[28px] w-[28px] shrink-0 place-items-center rounded-[5px] border border-[rgba(255,255,255,.08)] bg-transparent text-muted-foreground transition-colors hover:bg-[#17171c] hover:text-foreground"
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loadingRepos ? (
            <div className="flex h-32 items-center justify-center">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="flex flex-col gap-[14px]">

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-[13px] w-[13px] -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search repositories…"
                  className="h-[36px] w-full rounded-[6px] border border-[rgba(255,255,255,.08)] bg-[#17171c] pl-[34px] pr-3 font-[inherit] text-[12px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[#b5e84a]"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Scan mode */}
              <div>
                <div className="mb-[8px] font-mono text-[10px] uppercase tracking-[.07em] text-muted-foreground">Scan Mode</div>
                <ScanModeChoice value={scanMode} onChange={setScanMode} disabled={scanning} />
              </div>

              {/* Usage strip */}
              {billingStatus && (
                <div className="rounded-[6px] border border-[rgba(255,255,255,.08)] bg-[#17171c] p-[10px_14px]">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[.07em] text-muted-foreground">Monthly Slots</span>
                    <span className="font-mono text-[11px] text-foreground">
                      {billingStatus.active_repos}
                      {' '}/{' '}
                      {billingStatus.max_repos === -1 ? 'Unlimited' : billingStatus.max_repos}
                    </span>
                  </div>
                  {billingStatus.max_repos !== -1 && (
                    <div className="mt-[8px] h-[4px] w-full overflow-hidden rounded-full bg-[rgba(255,255,255,.08)]">
                      <div
                        className="h-full rounded-full bg-[#b5e84a] transition-all"
                        style={{ width: `${Math.min(usagePct, 100)}%` }}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Active repos */}
              {activeRepositories.length > 0 && (
                <div>
                  <div className="mb-[8px] flex items-center justify-between">
                    <div className="flex items-center gap-[7px]">
                      <span className="font-mono text-[10px] uppercase tracking-[.07em] text-muted-foreground">Ready to Scan</span>
                      <span className="rounded-[3px] border border-[rgba(255,255,255,.08)] bg-[#1e1e24] px-[5px] py-[1px] font-mono text-[9px] text-muted-foreground">
                        {activeRepositories.length}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleToggleAll}
                      className="inline-flex items-center gap-[5px] border-0 bg-transparent font-mono text-[10px] text-muted-foreground transition-colors hover:text-foreground cursor-pointer font-[inherit] p-0"
                    >
                      <span className={`grid h-[13px] w-[13px] place-items-center rounded-[3px] border ${allActiveSelected ? 'border-[#b5e84a] bg-[#b5e84a]' : 'border-[rgba(255,255,255,.2)] bg-transparent'}`}>
                        {allActiveSelected && (
                          <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#111115" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </span>
                      Select All
                    </button>
                  </div>

                  <div className="flex flex-col gap-[6px]">
                    {activeRepositories.map((repo) => {
                      const selected = selectedRepoIds.has(repo.id);
                      const plat = PLATFORM_BADGE[repo.platform_type] ?? PLATFORM_BADGE.github;
                      return (
                        <div
                          key={repo.id}
                          onClick={() => handleToggleRepository(repo.id)}
                          className={`flex cursor-pointer items-center gap-[10px] rounded-[8px] border p-[10px_12px] transition-all ${
                            selected
                              ? 'border-[rgba(181,232,74,.35)] bg-[rgba(181,232,74,.06)]'
                              : 'border-[rgba(255,255,255,.08)] bg-[#17171c] hover:border-[rgba(255,255,255,.16)]'
                          }`}
                        >
                          {/* Custom checkbox */}
                          <span
                            className={`grid h-[14px] w-[14px] shrink-0 place-items-center rounded-[3px] border transition-all ${
                              selected
                                ? 'border-[#b5e84a] bg-[#b5e84a]'
                                : 'border-[rgba(255,255,255,.2)] bg-transparent'
                            }`}
                            onClick={(e) => { e.stopPropagation(); handleToggleRepository(repo.id); }}
                          >
                            {selected && (
                              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#111115" strokeWidth="3">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </span>

                          {/* Repo info */}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-[6px]">
                              <span className="text-[13px] font-semibold truncate">{repo.name}</span>
                              <span
                                className="shrink-0 rounded-[3px] border border-[rgba(255,255,255,.08)] px-[5px] py-[1px] font-mono text-[9px] capitalize"
                                style={{ background: plat.bg, color: plat.color }}
                              >
                                {repo.platform_type.replace('_', ' ')}
                              </span>
                              {repo.is_private && (
                                <Lock className="h-[10px] w-[10px] shrink-0 text-muted-foreground" />
                              )}
                            </div>
                            <span className="block truncate font-mono text-[10px] text-muted-foreground">{repo.full_name}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Inactive repos */}
              {inactiveRepositories.length > 0 && (
                <div>
                  <div className="mb-[8px] flex items-center gap-[7px]">
                    <span className="font-mono text-[10px] uppercase tracking-[.07em] text-muted-foreground">Plan Locked / Inactive</span>
                    <Lock className="h-[10px] w-[10px] text-muted-foreground" />
                  </div>
                  <div className="flex flex-col gap-[6px] opacity-50">
                    {inactiveRepositories.map((repo) => (
                      <div
                        key={repo.id}
                        className="flex items-center justify-between rounded-[8px] border border-[rgba(255,255,255,.08)] bg-[#17171c] p-[10px_12px]"
                      >
                        <div className="flex items-center gap-[8px] min-w-0">
                          <Lock className="h-[12px] w-[12px] shrink-0 text-muted-foreground" />
                          <div className="min-w-0">
                            <div className="text-[12px] font-semibold text-muted-foreground truncate">{repo.name}</div>
                            <div className="font-mono text-[10px] text-muted-foreground truncate">{repo.full_name}</div>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="ml-2 shrink-0 border-0 bg-transparent font-mono text-[10px] text-muted-foreground transition-colors hover:text-foreground cursor-pointer font-[inherit] p-0"
                          onClick={() => { onOpenChange(false); navigate('/repositories?filter=inactive'); }}
                        >
                          Manage
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-[6px] flex items-center gap-[5px] font-mono text-[10px] text-muted-foreground px-1">
                    <AlertTriangle className="h-[10px] w-[10px] shrink-0" />
                    Inactive repositories must be enabled to scan.
                  </div>
                </div>
              )}

              {filteredRepositories.length === 0 && !loadingRepos && (
                <div className="py-8 text-center font-mono text-[11px] text-muted-foreground">
                  No repositories match your search.
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <DialogFooter className="border-t border-[rgba(255,255,255,.08)] px-5 py-[14px] mt-0 sm:space-x-0">
          <div className="flex w-full items-center justify-between gap-3">
            <span className="font-mono text-[10px] text-muted-foreground">
              {selectedRepoIds.size} {selectedRepoIds.size === 1 ? 'repository' : 'repositories'} selected
            </span>
            <div className="flex gap-[6px]">
              <button
                type="button"
                onClick={() => { onOpenChange(false); setSelectedRepoIds(new Set()); }}
                className="inline-flex h-[32px] items-center gap-[6px] rounded-[6px] border border-[rgba(255,255,255,.08)] bg-transparent px-[14px] font-[inherit] font-mono text-[11px] text-muted-foreground transition-colors hover:bg-[#17171c] hover:text-foreground"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleScan}
                disabled={selectedRepoIds.size === 0 || scanning}
                className="inline-flex h-[32px] items-center gap-[6px] rounded-[6px] border border-[rgba(181,232,74,.35)] bg-[rgba(181,232,74,.08)] px-[14px] font-[inherit] font-mono text-[11px] font-semibold text-[#b5e84a] transition-colors hover:bg-[rgba(181,232,74,.15)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {scanning ? (
                  <><RefreshCw className="h-[10px] w-[10px] animate-spin" /> Scanning…</>
                ) : (
                  <><ScanLine className="h-[10px] w-[10px]" /> {scanMode === 'full' ? 'Full scan' : 'Scan'}{selectedRepoIds.size > 0 ? ` (${selectedRepoIds.size})` : ''}</>
                )}
              </button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScanRepositoryModal;
